import { Injectable, computed, inject, signal } from '@angular/core';
import { type Message } from '@ag-ui/client';
import { ToastrService } from 'ngx-toastr';
import { TranslateService } from '@ngx-translate/core';
import {
  ChatStatus,
  ChatView,
  ToolCallStatus,
  type ToolCallLine,
} from '../models/chat';
import { AgUiChatTransport } from './ag-ui-chat-transport.service';
import { ChatHistoryService } from './chat-history.service';

@Injectable({
  providedIn: 'root',
})
export class ChatStateService {
  private readonly chatTransport = inject(AgUiChatTransport);
  private readonly chatHistory = inject(ChatHistoryService);
  private readonly toastService = inject(ToastrService);
  private readonly translateService = inject(TranslateService);

  private readonly _isOpen = signal(false);
  private readonly _view = signal<ChatView>(ChatView.Chat);
  private readonly _status = signal<ChatStatus>(ChatStatus.Start);
  private readonly _messages = signal<Message[]>([]);
  private readonly _toolCalls = signal<ToolCallLine[]>([]);

  readonly isOpen = this._isOpen.asReadonly();
  readonly view = this._view.asReadonly();
  readonly status = this._status.asReadonly();
  readonly messages = this._messages.asReadonly();
  readonly toolCalls = this._toolCalls.asReadonly();

  readonly hasConversation = computed(() => this.messages().length > 0);
  readonly isStreaming = computed(() => {
    const status = this._status();
    return (
      status === ChatStatus.Working ||
      status === ChatStatus.Thinking ||
      status === ChatStatus.Searching ||
      status === ChatStatus.Streaming
    );
  });

  get sessionId(): string {
    return this.chatTransport.agent.threadId;
  }

  private lastPrompt = '';

  open(): void {
    this._isOpen.set(true);
  }

  close(): void {
    this._isOpen.set(false);
  }

  toggle(): void {
    this._isOpen.update((open) => !open);
  }

  showHistory(): void {
    this._view.set(ChatView.History);
  }

  showChat(): void {
    this._view.set(ChatView.Chat);
  }

  deleteSession(id: string): void {
    const isCurrentSession = id === this.chatTransport.agent.threadId;
    this.chatHistory.deleteSession(id).subscribe({
      next: () => {
        if (isCurrentSession) {
          this.startNewChat();
        }
      },
      error: () =>
        this.toastService.error(
          this.translateService.instant('chat.history.deleteFailed'),
        ),
    });
  }

  loadSession(sessionId: string): void {
    this.chatHistory.loadSession(sessionId).subscribe({
      next: (messages) => {
        if (!messages) {
          return;
        }

        this.chatTransport.abort();

        this.chatTransport.agent.setMessages([...messages]);
        this.chatTransport.agent.threadId = sessionId;

        this.lastPrompt = '';
        this._toolCalls.set([]);
        this._messages.set([...messages]);
        this._status.set(ChatStatus.Answer);
        this._view.set(ChatView.Chat);
      },
      error: () =>
        this.toastService.error(
          this.translateService.instant('chat.history.loadFailed'),
        ),
    });
  }

  submit(text: string): void {
    const trimmed = text.trim();
    if (!trimmed || this.isStreaming()) {
      return;
    }

    this.lastPrompt = trimmed;
    this.chatTransport.agent.addMessage({
      id: crypto.randomUUID(),
      role: 'user',
      content: trimmed,
    });
    this._messages.set([...this.chatTransport.agent.messages]);
    this.startRun();
  }

  stop(): void {
    this.chatTransport.abort();
    this._status.set(ChatStatus.Stopped);
  }

  retry(): void {
    this.rerun();
  }

  continueAnswer(): void {
    this.rerun();
  }

  startNewChat(): void {
    this.chatTransport.abort();
    this.chatTransport.resetThread();
    this.lastPrompt = '';
    this._messages.set([]);
    this._toolCalls.set([]);
    this._status.set(ChatStatus.Start);
  }

  private rerun(): void {
    if (!this.lastPrompt) {
      return;
    }
    this.startRun();
  }

  private startRun(): void {
    this._status.set(ChatStatus.Working);
    this.chatTransport
      .run({
        onRunStartedEvent: () => {
          this._status.set(ChatStatus.Working);
        },
        onReasoningStartEvent: () => {
          this._status.set(ChatStatus.Thinking);
        },
        onReasoningEndEvent: () => {
          this._status.set(ChatStatus.Working);
        },
        onToolCallStartEvent: ({ event }) => {
          this._status.set(ChatStatus.Searching);
          this._toolCalls.update((calls) => [
            ...calls,
            {
              id: event.toolCallId,
              name: event.toolCallName,
              status: ToolCallStatus.Calling,
              parentMessageId: event.parentMessageId,
            },
          ]);
        },
        onToolCallResultEvent: ({ event }) => {
          this._toolCalls.update((calls) =>
            calls.map((call) =>
              call.id === event.toolCallId
                ? { ...call, status: ToolCallStatus.Called }
                : call,
            ),
          );
        },
        onTextMessageContentEvent: () => {
          this._status.set(ChatStatus.Streaming);
        },
        onMessagesChanged: ({ messages }) => {
          this._messages.set([...messages]);
        },
        onRunErrorEvent: () => {
          this._status.set(ChatStatus.ErrorAnswer);
        },
        onRunFinishedEvent: () => {
          this._status.set(ChatStatus.Answer);
        },
        onCustomEvent: () => {
          this._status.set(ChatStatus.Streaming);
        },
      })
      .catch(() => {
        this._status.set(ChatStatus.ErrorAnswer);
      });
  }
}
